/**
 * /help/docs/{path} — In-app markdown viewer for help docs.
 *
 * The path after `/help/docs/` is the relative path under `docs/help/`,
 * e.g. `/help/docs/payroll-year-end/setup-iris.md`.
 *
 * The backend (GET /api/help/doc?path=) handles authorization and
 * scoping — anything outside `docs/help/` is rejected with 400.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Breadcrumbs, Button, Chip, CircularProgress, Paper, Stack, Typography,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";
import ChatIcon from "@mui/icons-material/Chat";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/help_/docs/$")({
  component: HelpDocPage,
});

interface DocBody {
  path: string;
  title: string;
  audience: string | null;
  priority: string;
  keywords: string[];
  body: string;
}

function HelpDocPage() {
  const params = Route.useParams() as { _splat?: string };
  // _splat is the catchall after /help/docs/
  const tail = params._splat ?? "";
  const fullPath = tail.startsWith("docs/help/") ? tail : `docs/help/${tail}`;

  const qc = useQueryClient();
  const [generateError, setGenerateError] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ["help-doc", fullPath],
    queryFn: async () => {
      const url = `/api/help/doc?path=${encodeURIComponent(fullPath)}`;
      return (await api(url)) as DocBody;
    },
  });
  const { data, isLoading, error } = docQuery;

  const generateMutation = useMutation({
    mutationFn: async () => {
      return api<{ path: string; title: string; body: string }>("/api/help/generate", {
        method: "POST",
        body: JSON.stringify({ path: fullPath }),
        headers: { "Content-Type": "application/json" },
      });
    },
    onSuccess: () => {
      setGenerateError(null);
      // Invalidate so the doc query refetches from disk
      qc.invalidateQueries({ queryKey: ["help-doc", fullPath] });
    },
    onError: (err: Error) => {
      setGenerateError(err.message ?? "Generation failed.");
    },
  });

  // Breadcrumbs from path
  const segments = fullPath.split("/").filter((s) => s && s !== "docs");

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          to="/help"
          style={{ display: "flex", alignItems: "center", gap: 4, color: "inherit", textDecoration: "none" }}
        >
          <HomeIcon fontSize="small" /> Help
        </Link>
        {segments.map((s, i) => (
          <Typography key={i} variant="body2" color={i === segments.length - 1 ? "text.primary" : "text.secondary"}>
            {s}
          </Typography>
        ))}
      </Breadcrumbs>

      {isLoading && <CircularProgress />}

      {error && (() => {
        // 404 = the doc just hasn't been written yet. Treat as a soft
        // "no help available" — not all parts of the app have docs and
        // a stack-trace-looking error is jarring. Anything else (500,
        // network, etc.) is still a real error worth surfacing.
        // ofetch puts the HTTP status on `error.response.status`; the
        // serialized message also contains "404 Not Found", so we
        // belt-and-suspenders both.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const status = (error as any)?.response?.status;
        const msg = String((error as Error).message ?? "");
        const isNotFound = status === 404 || /\b404\b/.test(msg);
        if (isNotFound) {
          return (
            <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
              <DescriptionIcon
                sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
              />
              <Typography variant="h6" gutterBottom>
                No help available yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No guide has been written for this page yet.
              </Typography>

              {/* Auto-generate action */}
              {generateMutation.isPending ? (
                <Stack alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="caption" color="text.secondary">
                    Writing guide with AI — this takes a few seconds…
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" justifyContent="center" spacing={2} sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AutoFixHighIcon />}
                    onClick={() => generateMutation.mutate()}
                    disabled={generateMutation.isPending}
                  >
                    Generate with AI
                  </Button>
                  <Link
                    to="/chat"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <ChatIcon fontSize="small" /> Ask Bynaus instead
                  </Link>
                </Stack>
              )}

              {generateError && (
                <Alert severity="error" sx={{ mt: 1, textAlign: "left" }}>
                  {generateError}
                </Alert>
              )}
            </Paper>
          );
        }
        return (
          <Alert severity="error">
            Could not load this help doc: {msg}
          </Alert>
        );
      })()}

      {data && (
        <Paper variant="outlined" sx={{ p: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DescriptionIcon color="primary" />
              <Typography variant="caption" color="text.secondary">
                {data.path}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              {data.audience && <Chip size="small" label={data.audience} />}
              {data.priority && data.priority !== "normal" && (
                <Chip size="small" label={data.priority} color={data.priority === "high" ? "warning" : "default"} />
              )}
            </Stack>
          </Stack>

          <Box
            sx={{
              "& h1": { fontSize: "1.75rem", fontWeight: 700, mt: 0, mb: 2 },
              "& h2": { fontSize: "1.35rem", fontWeight: 600, mt: 4, mb: 1.5, borderBottom: "1px solid", borderColor: "divider", pb: 0.5 },
              "& h3": { fontSize: "1.1rem", fontWeight: 600, mt: 3, mb: 1 },
              "& p": { lineHeight: 1.7, mb: 1.5 },
              "& ul, & ol": { pl: 3, mb: 1.5 },
              "& li": { mb: 0.5, lineHeight: 1.6 },
              "& code": {
                bgcolor: "action.hover",
                px: 0.6,
                py: 0.2,
                borderRadius: 0.5,
                fontSize: "0.875em",
                fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
              },
              "& pre": {
                bgcolor: "background.default",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 2,
                overflow: "auto",
                "& code": { bgcolor: "transparent", p: 0 },
              },
              "& blockquote": {
                borderLeft: "4px solid",
                borderColor: "primary.main",
                pl: 2,
                ml: 0,
                my: 2,
                color: "text.secondary",
              },
              "& table": {
                borderCollapse: "collapse",
                width: "100%",
                my: 2,
                "& th, & td": { border: "1px solid", borderColor: "divider", px: 1.5, py: 1, textAlign: "left" },
                "& th": { bgcolor: "action.hover", fontWeight: 600 },
              },
              "& a": { color: "primary.main" },
              "& hr": { my: 3, border: "none", borderTop: "1px solid", borderColor: "divider" },
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children, ...props }) => {
                  const h = href ?? "";
                  // App-relative links (e.g. /payroll/year-end) — use Link
                  if (h.startsWith("/") && !h.startsWith("//")) {
                    return (
                      <Link to={h as any} {...(props as any)}>
                        {children}
                      </Link>
                    );
                  }
                  // Sibling .md → resolve to /help/docs/<resolved>
                  if (h.endsWith(".md") && !h.startsWith("http")) {
                    const folder = fullPath.split("/").slice(0, -1).join("/");
                    const resolved = h.startsWith("../") || h.startsWith("./")
                      ? new URL(h, `https://x/${folder}/`).pathname.slice(1)
                      : `${folder}/${h}`;
                    const helpPath = resolved.startsWith("docs/help/")
                      ? resolved.slice("docs/help/".length)
                      : resolved;
                    return (
                      <Link to={`/help/docs/${helpPath}` as any} {...(props as any)}>
                        {children}
                      </Link>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noreferrer" {...props}>
                      {children}
                    </a>
                  );
                },
              }}
            >
              {data.body}
            </ReactMarkdown>
          </Box>
        </Paper>
      )}

      <Box sx={{ mt: 3, color: "text.secondary", textAlign: "center" }}>
        <Typography variant="caption">
          Need a quick answer? <Link to="/chat"><ChatIcon fontSize="inherit" sx={{ verticalAlign: "middle" }} /> Ask Bynaus</Link> — it indexes every help doc here.
        </Typography>
      </Box>
    </Box>
  );
}
